import * as React from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  Typography,
  Rating,
  LinearProgress,
  Chip,
  Alert,
  Skeleton,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useTitle } from "../../hooks/useTitle";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import EmailIcon from "@mui/icons-material/Email";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
// import { TabPanel } from "@mui/lab";
import CompanyProfilePanel from "./CompanyProfilePanel";
import CompanyProjectsPanel from "./CompanyProjectsPanel";
import CompanyFundsPanel from "./CompanyFundsPanel";
import CompanyAccountPanel from "./CompanyAccountPanel";
import {
  useGetCompanyByIdQuery,
  useGetCompanyTotalJobSuccessRateByIdQuery,
  useGetCompanyTotalJobByIdQuery,
  useGetCompanyTotalSpentByIdQuery,
} from "../../services/company.service";
import { Company } from "../../types/company";
import { formatCash, formatMoney } from "../../utils/helper";
import { CardBase } from "../../components/global";

const ProfileStat = ({
  label,
  value,
  isLoading,
}: {
  label: string;
  value: string;
  isLoading?: boolean;
}) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: { xs: 1, sm: 2 },
      py: { xs: 0.75, sm: 1.5 },
      px: { xs: 0.75, sm: 2.5 },
      alignItems: "center",
      bgcolor: "background.paper",
      minWidth: 0,
      width: { xs: "100%", sm: "auto" },
      transition: "all 0.2s",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      boxSizing: "border-box",
      height: "100%",
    }}
  >
    {isLoading ? (
      <Box sx={{ textAlign: "center", py: 0.5 }}>
        <CircularProgress size={16} />
      </Box>
    ) : (
      <Typography
        textAlign={"center"}
        fontSize={{ xs: 12, sm: 18 }}
        fontWeight={600}
        letterSpacing={0.5}
        sx={{
          color: "#F4A259",
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {value}
      </Typography>
    )}
    <Typography
      textAlign={"center"}
      fontSize={{ xs: 9, sm: 12 }}
      color="text.secondary"
      sx={{ mt: 0.25 }}
      noWrap
    >
      {label}
    </Typography>
  </Box>
);

const ProfileMeta = ({
  icon,
  value,
}: {
  icon: React.ReactNode;
  value: string;
}) => (
  <Chip
    icon={icon as React.ReactElement}
    label={value}
    size="small"
    variant="outlined"
    sx={{
      mr: { xs: 0.5, sm: 1 },
      mb: { xs: 0.5, sm: 1 },
      fontSize: { xs: "0.75rem", sm: "0.875rem" },
      height: { xs: 24, sm: 32 },
      color: "text.secondary",
      borderColor: "divider",
      "& .MuiChip-icon": {
        color: "text.secondary",
        fontSize: { xs: "1rem", sm: "1.25rem" },
      },
    }}
  />
);

const ProfileHeader = ({
  data,
  dataTotalJobSuccessRate,
  dataTotalJob,
  dataTotalSpent,
  isLoadingTotalJobSuccessRate,
  isLoadingTotalJob,
  isLoadingTotalSpent,
  loading,
}: {
  data?: Company;
  dataTotalJobSuccessRate: any;
  dataTotalJob: any;
  dataTotalSpent: any;
  isLoadingTotalJobSuccessRate: boolean;
  isLoadingTotalJob: boolean;
  isLoadingTotalSpent: boolean;
  loading: boolean;
}) => {
  if (loading)
    return (
      <CardBase useGlassCard={false} sx={{ mb: 3 }}>
        <Stack direction="column" spacing={2}>
          <Skeleton variant="text" sx={{ width: "30%", height: 40 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={150} height={32} sx={{ borderRadius: 1 }} />
          </Stack>
        </Stack>
      </CardBase>
    );

  return (
    <CardBase useGlassCard={false} sx={{ mb: { xs: 2, sm: 3 }, p: { xs: 1.5, sm: 2.5 } }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={{ xs: 1.5, sm: 2 }}
        sx={{ width: "100%" }}
      >
        <Avatar
          variant="square"
          sx={{
            bgcolor: "#F4A259",
            width: { xs: 60, sm: 100 },
            height: { xs: 60, sm: 100 },
            fontSize: { xs: 24, sm: 40 },
            flexShrink: 0,
          }}
          src={data?.logo?.[0]?.original_url || undefined}
        >
          {data?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Stack direction="column" flex={1} spacing={{ xs: 1, sm: 2 }} sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.5, sm: 1 }}
            flexWrap="wrap"
          >
            <Typography
              variant="h5"
              sx={{
                color: "#F4A259",
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.5rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {data?.name}
            </Typography>
            <Rating
              value={data?.total_rating || 0}
              readOnly
              size="small"
              sx={{ flexShrink: 0 }}
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            spacing={{ xs: 0.5, sm: 1 }}
          >
            <ProfileMeta
              icon={<PersonPinCircleIcon />}
              value={`${data?.city || "Unknown City"}, ${data?.state || "Unknown State"
                }`}
            />
            <ProfileMeta
              icon={<PhoneAndroidIcon />}
              value={`${data?.phone_number || "No Phone"}`}
            />
          </Stack>
          <Grid container spacing={{ xs: 0.5, sm: 1 }} sx={{ mt: { xs: 0, sm: 0 } }}>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Balance"
                value={
                  !!data?.balance_decrypt
                    ? `$${formatMoney(data?.balance_decrypt || 0)}`
                    : `$0`
                }
              />
            </Grid>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Uplift"
                value={
                  !!data?.percentage_uplift
                    ? `${data?.percentage_uplift}%`
                    : "(not set)"
                }
              />
            </Grid>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Job Rating"
                value={`${+Number(data?.total_rating || 0).toFixed(1)} ☆`}
              />
            </Grid>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Job Success"
                value={`${+Number(dataTotalJobSuccessRate?.total || 0).toFixed(2)}%`}
                isLoading={isLoadingTotalJobSuccessRate}
              />
            </Grid>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Jobs"
                value={`${dataTotalJob?.total || 0}`}
                isLoading={isLoadingTotalJob}
              />
            </Grid>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Total Spent"
                value={`$${formatCash(Number(dataTotalSpent?.total || 0))}`}
                isLoading={isLoadingTotalSpent}
              />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </CardBase>
  );
};

const TabPanel = ({
  children,
  value,
  index,
}: {
  children?: React.ReactNode;
  value: string;
  index: string;
}) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

const CompanyDetailPage = () => {
  const { companyId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = React.useState("profile");

  const { data, isLoading: isLoadingCompany } = useGetCompanyByIdQuery(
    parseInt(companyId!),
    {
      skip: !companyId,
    }
  );

  const {
    data: dataTotalJobSuccessRate,
    isLoading: isLoadingTotalJobSuccessRate,
  } = useGetCompanyTotalJobSuccessRateByIdQuery(parseInt(companyId!), {
    skip: !companyId,
  });

  const { data: dataTotalJob, isLoading: isLoadingTotalJob } =
    useGetCompanyTotalJobByIdQuery(parseInt(companyId!), {
      skip: !companyId,
    });

  const { data: dataTotalSpent, isLoading: isLoadingTotalSpent } =
    useGetCompanyTotalSpentByIdQuery(parseInt(companyId!), {
      skip: !companyId,
    });

  useTitle(`Client #${data?.id}`, `${data?.name} | Client`);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  if (!data && !isLoadingCompany) {
    return (
      <CardBase useGlassCard={false}>
        <Alert severity="error">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Client Not Found
          </Typography>
          <Typography variant="body2">
            The client you are looking for does not exist or has been removed.
          </Typography>
        </Alert>
      </CardBase>
    );
  }

  return (
    <Box>
      <ProfileHeader
        data={data}
        dataTotalJobSuccessRate={dataTotalJobSuccessRate}
        dataTotalJob={dataTotalJob}
        dataTotalSpent={dataTotalSpent}
        isLoadingTotalJobSuccessRate={isLoadingTotalJobSuccessRate}
        isLoadingTotalJob={isLoadingTotalJob}
        isLoadingTotalSpent={isLoadingTotalSpent}
        loading={isLoadingCompany || false}
      />
      {isLoadingCompany && <LinearProgress sx={{ mb: 2 }} />}
      <CardBase useGlassCard={false} sx={{ p: 0 }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          textColor="inherit"
          sx={{
            px: { xs: 1, sm: 2 },
            pt: { xs: 0.5, sm: 1 },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              minHeight: { xs: 44, sm: 48 },
              fontSize: { xs: "0.875rem", sm: "0.95rem" },
              px: { xs: 1, sm: 2 },
              color: "text.secondary",
            },
            "& .Mui-selected": {
              color: "#F4A259 !important",
              fontWeight: 600,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#F4A259",
            },
            "& .MuiTabs-scrollButtons": {
              color: "#F4A259",
            },
          }}
        >
          <Tab label="Overview" value={"profile"} />
          <Tab label="Jobs" value={"jobs"} />
          <Tab label="Finances" value={"finances"} />
          <Tab label="Managers" value={"account"} />
        </Tabs>
        <Box sx={{ px: { xs: 1, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
          <TabPanel value={tab} index="profile">
            <CompanyProfilePanel data={data} />
          </TabPanel>
          <TabPanel value={tab} index="jobs">
            <CompanyProjectsPanel data={data} />
          </TabPanel>
          <TabPanel value={tab} index="finances">
            <CompanyFundsPanel data={data} />
          </TabPanel>
          <TabPanel value={tab} index="account">
            <CompanyAccountPanel data={data} />
          </TabPanel>
        </Box>
      </CardBase>
    </Box>
  );
};
export default CompanyDetailPage;
